import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { queries, formatMoney } from '@/data';
import { useDetailsStore } from '@/features/details';
import { useDefaultSaveDistrict } from '@/features/shortlist';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { aggregateDistrict } from './aggregate';
import { uiLang } from '@/i18n/lang';

function fmt(value: number | null | undefined, digits = 1, suffix = ''): string {
  if (value == null || !isFinite(value)) return '—';
  return `${value.toFixed(digits)}${suffix}`;
}

function fmtInt(value: number | null | undefined, suffix = '', lang: 'en' | 'ru' = 'ru'): string {
  if (value == null || !isFinite(value)) return '—';
  return `${Math.round(value).toLocaleString(lang === 'ru' ? 'ru' : 'en')}${suffix}`;
}

const UNITS = {
  en: { km: ' km', m: ' m', mm: ' mm', h: ' h', mbps: ' Mbps', ms: ' ms', perKm2: ' ppl/km²' },
  ru: { km: ' км', m: ' м', mm: ' мм', h: ' ч', mbps: ' Мбит/с', ms: ' мс', perKm2: ' чел/км²' },
} as const;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between border-b border-slate-50 py-1 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}

function KpiCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded bg-slate-50 px-1 py-1 text-center">
      <div className="text-[9px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-xs font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function Section({
  title,
  emoji,
  defaultOpen = true,
  children,
}: {
  title: string;
  emoji: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details open={defaultOpen} className="group border-b border-slate-100 last:border-b-0">
      <summary className="-mx-2 flex cursor-pointer list-none items-center gap-1.5 rounded px-2 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900">
        {emoji && <span aria-hidden="true" className="shrink-0">{emoji}</span>}
        <span className="flex-1 truncate">{title}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-150 group-open:rotate-90"
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </summary>
      <div className="pb-2">{children}</div>
    </details>
  );
}

function DistrictSaveButton({ distCode }: { distCode: string }) {
  const { t } = useTranslation();
  const { isSaved, toggle } = useDefaultSaveDistrict(distCode);
  return (
    <button
      type="button"
      onClick={toggle}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
        isSaved
          ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
      }`}
      title={isSaved ? t('shortlist.unsave', { defaultValue: 'Remove from saved' }) : t('shortlist.save', { defaultValue: 'Save' })}
      aria-pressed={isSaved}
    >
      {isSaved ? '★' : '☆'} {isSaved ? t('shortlist.saved', { defaultValue: 'Saved' }) : t('shortlist.save', { defaultValue: 'Save' })}
    </button>
  );
}

export function DistrictDetailsPanel() {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'ru' = uiLang(i18n.language);
  const u = UNITS[lang];
  const openDist = useDetailsStore((s) => s.openDist);
  const close = useDetailsStore((s) => s.close);

  const { data: meta } = useQuery(queries.meta());
  const version = meta?.version ?? '';
  const { data: fullIndex } = useQuery({
    ...queries.fullIndex(version),
    enabled: !!version && openDist !== null,
  });
  const { data: distMeta } = useQuery({
    ...queries.districtsMeta(version),
    enabled: !!version && openDist !== null,
  });

  const members = useMemo(
    () => (openDist && fullIndex ? fullIndex.byDist.get(openDist) ?? [] : []),
    [openDist, fullIndex],
  );
  const agg = useMemo(() => aggregateDistrict(members), [members]);
  const { unitPlural } = useUnitTerms();

  // Escape closes
  useEffect(() => {
    if (openDist === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openDist, close]);

  if (openDist === null) return null;

  const info = distMeta?.get(openDist);
  const name =
    info?.name_alt ? `${info.name} / ${info.name_alt}` : info?.name ?? openDist;
  const kindLabel =
    t(`district.kind.${info?.kind || 'sll'}`);

  return (
    <>
      <div onClick={close} className="fixed inset-0 z-[1009] bg-black/30 sm:hidden" aria-hidden="true" />
      <aside className="absolute inset-x-0 bottom-0 z-[1010] flex max-h-[80dvh] flex-col overflow-hidden rounded-t-2xl bg-white/95 shadow-2xl backdrop-blur-sm pb-safe sm:inset-x-auto sm:bottom-40 sm:right-4 sm:z-[1000] sm:w-80 sm:max-h-[calc(100%-15rem)] sm:rounded-lg">
        <div className="flex justify-center py-1.5 sm:hidden" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-slate-300" />
        </div>
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-slate-900">{name}</div>
            <div className="truncate text-[11px] text-slate-500">
              {kindLabel}
              {agg ? ` · ${unitPlural}: ${agg.nComuni}` : ''}
              {info?.parent ? ` · ${info.parent}` : ''}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <DistrictSaveButton distCode={openDist} />
            <button
              onClick={close}
              className="flex h-10 w-10 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700"
              title={t('details.closeTooltip', { defaultValue: 'Close' })}
              aria-label={t('details.close', { defaultValue: 'Close' })}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-3">
          {!agg ? (
            <div className="py-4 text-center text-sm text-slate-400">{t('details.loading', { defaultValue: 'Loading…' })}</div>
          ) : (
            <>
              <p className="px-0 pt-2 text-[11px] leading-snug text-slate-400">
                {t('district.aggNote', {
                  defaultValue:
                    'Aggregated across member comuni: totals are summed; climate, internet and income are population-weighted averages.',
                })}
              </p>
              {/* KPI row */}
              <div className="grid grid-cols-4 gap-1 py-2">
                <KpiCell label={t('details.row.population', { defaultValue: 'Население' })} value={fmtInt(agg.pop, '', lang)} />
                <KpiCell label={t('details.kpi.tMean', { defaultValue: 'T° ср' })} value={fmt(agg.climate.t_mean, 1, '°')} />
                <KpiCell label={t('details.kpi.altitude', { defaultValue: 'Высота' })} value={fmtInt(agg.alt, u.m, lang)} />
                <KpiCell label={t('details.kpi.internet', { defaultValue: 'Интернет' })} value={fmt(agg.internet.dl, 0, 'М')} />
              </div>

              {/* Climate */}
              <Section title={t('details.section.climate', { defaultValue: 'Климат' })} emoji="🌤">
                <Row label={t('details.row.tMean', { defaultValue: 'Средняя T° (год)' })} value={fmt(agg.climate.t_mean, 1, '°C')} />
                <Row label={t('details.row.tJul', { defaultValue: 'T° июля' })} value={fmt(agg.climate.t_jul, 1, '°C')} />
                <Row label={t('details.row.tJan', { defaultValue: 'T° января' })} value={fmt(agg.climate.t_jan, 1, '°C')} />
                <Row label={t('details.row.hotDays', { defaultValue: 'Жарких дней' })} value={fmt(agg.climate.hot_days, 1)} />
                <Row label={t('details.row.humidity', { defaultValue: 'Влажность' })} value={fmt(agg.climate.hum, 0, '%')} />
                <Row label={t('details.row.precip', { defaultValue: 'Осадки' })} value={fmtInt(agg.climate.precip, u.mm, lang)} />
                <Row label={t('details.row.snowDays', { defaultValue: 'Снежных дней' })} value={fmt(agg.climate.snow, 0)} />
                <Row label={t('details.row.sunnyDays', { defaultValue: 'Солнечных дней' })} value={fmt(agg.climate.sunny, 0)} />
              </Section>

              {/* Demo */}
              <Section title={t('details.section.demo', { defaultValue: 'Демография' })} emoji="👥">
                <Row label={t('details.row.population', { defaultValue: 'Население' })} value={fmtInt(agg.pop, '', lang)} />
                <Row label={t('details.row.density', { defaultValue: 'Плотность' })} value={fmt(agg.density, 1, u.perKm2)} />
                <Row label={unitPlural} value={agg.nComuni} />
              </Section>

              {/* Infra */}
              <Section title={t('details.section.infra', { defaultValue: 'Инфраструктура' })} emoji="🏥" defaultOpen={false}>
                <Row label={t('details.row.toHospital', { defaultValue: 'До больницы' })} value={fmt(agg.infra.hosp, 1, u.km)} />
                <Row label={t('details.row.toPharmacy', { defaultValue: 'До аптеки' })} value={fmt(agg.infra.pharm, 1, u.km)} />
                <Row label={t('details.row.toSchool', { defaultValue: 'До школы' })} value={fmt(agg.infra.school, 1, u.km)} />
                <Row label={t('details.row.toTrain', { defaultValue: 'До ЖД-станции' })} value={fmt(agg.infra.train, 1, u.km)} />
                <Row label={t('details.row.toAirport', { defaultValue: 'До аэропорта' })} value={fmt(agg.infra.air, 0, u.km)} />
              </Section>

              {/* Internet */}
              <Section title={t('details.section.internet', { defaultValue: 'Интернет' })} emoji="📡" defaultOpen={false}>
                <Row label={t('details.row.dl', { defaultValue: 'Скорость download' })} value={fmt(agg.internet.dl, 1, u.mbps)} />
                <Row label={t('details.row.ul', { defaultValue: 'Скорость upload' })} value={fmt(agg.internet.ul, 1, u.mbps)} />
                <Row label={t('details.row.ping', { defaultValue: 'Пинг' })} value={fmt(agg.internet.ping, 0, u.ms)} />
              </Section>

              {/* Foreign residents */}
              {agg.foreign && agg.foreign.total > 0 && (
                <Section title={t('details.section.foreign', { defaultValue: 'Иностранцы' })} emoji="🌍" defaultOpen={false}>
                  <Row label={t('details.row.foreignTotal', { defaultValue: 'Всего иностранцев' })} value={fmtInt(agg.foreign.total, '', lang)} />
                  <Row label={t('details.row.foreignRu', { defaultValue: '🇷🇺 Россия' })} value={`${fmtInt(agg.foreign.ru, '', lang)} (${fmt(agg.foreign.ru_pct, 2, '%')})`} />
                  <Row label={t('details.row.foreignUa', { defaultValue: '🇺🇦 Украина' })} value={`${fmtInt(agg.foreign.ua, '', lang)} (${fmt(agg.foreign.ua_pct, 2, '%')})`} />
                  <Row label={t('details.row.foreignBy', { defaultValue: '🇧🇾 Беларусь' })} value={`${fmtInt(agg.foreign.by, '', lang)} (${fmt(agg.foreign.by_pct, 2, '%')})`} />
                </Section>
              )}

              {/* Econ */}
              {agg.econ && (
                <Section title={t('details.section.econ', { defaultValue: 'Экономика' })} emoji="💶" defaultOpen={false}>
                  <Row label={t('details.row.rent', { defaultValue: 'Аренда' })} value={formatMoney(agg.econ.rent_eur_m2y ?? null, meta, { perM2: true, perYear: true, decimals: 1, lang })} />
                  <Row label={t('details.row.sale', { defaultValue: 'Продажа' })} value={formatMoney(agg.econ.sale_eur_m2 ?? null, meta, { perM2: true, lang })} />
                  <Row label={t('details.row.income', { defaultValue: 'Средний доход' })} value={formatMoney(agg.econ.income_eur ?? null, meta, { perYear: true, lang })} />
                </Section>
              )}

              {/* Member comuni */}
              <Section title={t('district.members', { defaultValue: 'Comuni in this district' })} emoji="📍" defaultOpen={false}>
                <ul className="max-h-48 space-y-0.5 overflow-y-auto pb-1">
                  {agg.members.map((c) => (
                    <li key={c.istat} className="flex items-baseline justify-between py-0.5 text-xs">
                      <span className="truncate text-slate-700">{c.name}</span>
                      <span className="ml-2 shrink-0 font-mono text-[10px] text-slate-400">{fmtInt(c.pop, '', lang)}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
